import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AppShell from "@/pages/AppShell";
import PublicChantier from "@/pages/PublicChantier";
import SavRating from "@/pages/SavRating";
import PublicInterventionRating from "@/pages/PublicInterventionRating";
import PrintQrLabel from "@/pages/PrintQrLabel";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "sonner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#005CFF] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Detect the Emergent OAuth callback synchronously during render to avoid a
// race with the AuthProvider's initial /auth/me check. Reads from
// `useLocation().hash` (reactive) — never `window.location.hash`. Also
// supports `?session_id=` in the query string for browsers that strip
// fragments (some iOS PWAs).
function AppRouter() {
  const location = useLocation();
  const hasSessionId = location.hash?.includes("session_id=") || new URLSearchParams(location.search).has("session_id");
  if (hasSessionId) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/public/chantier/:token" element={<PublicChantier />} />
      <Route path="/rate/:token" element={<SavRating />} />
      <Route path="/avis/:token" element={<PublicInterventionRating />} />
      <Route path="/print/qr/:qr" element={<PrintQrLabel />} />
      <Route path="/login" element={<Public><Login /></Public>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/*" element={<Protected><AppShell /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark bg-[#0A0A0A] min-h-screen text-white">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
